import React, { useState, useEffect, useMemo, useDeferredValue } from "react"
import { useNavigate } from "react-router-dom"
import Table, { TableColumn } from "../../component/table/table"
import { agent } from "../../apiClient/api"
import CustomizedMenu from "../../component/muiMenu/muiMenu"
import IconsColor from "../../util/iconsColor"
import Popup from "../../component/popup/popup"
import utilStyles from "../../asset/styles/util/util.module.scss"
import styles from "../../asset/styles/transportation/dayTrip.module.scss"
import { useSnackbar } from "../../context/snackbarContext"
import Loader from "../../component/loader/loaderComponent"
import MainTabsTableHeader, { FilterGroup } from "../../component/mainTabsTableHeader/mainTabsTableHeader"
import { applyFilters, applySearch, cycleSort, sortRowsBy, uniqueOptionsFromRows } from "../../util/listFilters"
import { useListView, LIST_VIEW_KEYS } from "../../context/listFilterContext"
import { toTitleCase } from "util/textcase"
import { TablePaginationBar, useClientListPagination } from "../../component/tablePagination"
export interface DayTripPricingItem {
    trip_pricing_id: string
    day_trip_id: string
    day_trip_name: string
    seating_type_id: number
    seating_type_name: string
    price: number
    flag_active: boolean
}

export interface DayTripItem {
    day_trip_id: string
    day_trip_name: string
    city_id: number
    city_name: string
    day_trip_type_id: number
    day_trip_type_name: string
    starting_point_excursion_id: string | null
    starting_point_excursion_name: string | null
    starting_point_place_id: string
    starting_point_place_name: string
    end_point_excursion_id: string | null
    end_point_excursion_name: string | null
    end_point_place_id: string
    end_point_place_name: string
    flag_enable_sic: boolean
    sic_price_per_person: number | null
    flag_active: boolean
    day_trip_stop: unknown[]
    day_trip_pricing: DayTripPricingItem[]
    updated_at_timestamp?: string
    updated_by_user_id?: number
    updated_by_user_name?: string
}

const SEATING_COLUMNS: { key: string; label: string; seatingName: string }[] = [
    { key: "4s", label: "4s", seatingName: "4 Seater" },
    { key: "7s", label: "7s", seatingName: "7 Seater" },
    { key: "16s", label: "16s", seatingName: "16 Seater" },
    { key: "29s", label: "29s", seatingName: "29 Seater" },
    { key: "35s", label: "35s", seatingName: "35 Seater" },
    { key: "45s", label: "45s", seatingName: "45 Seater" },
]

function getPriceBySeating(pricing: DayTripPricingItem[] | undefined, seatingName: string): number | null {
    if (!pricing?.length) return null
    const item = pricing.find((p) => p.seating_type_name === seatingName)
    return item != null ? item.price : null
}

function formatPrice(value: number | null): string {
    if (value == null) return "—"
    const n = Number(value)
    if (Number.isNaN(n)) return "—"
    return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(2)
}

function getStartPointName(row: DayTripItem): string {
    return row.starting_point_excursion_id === null ? row.starting_point_place_name : (row.starting_point_excursion_name ?? "")
}

function getEndPointName(row: DayTripItem): string {
    return row.end_point_excursion_id === null ? row.end_point_place_name : (row.end_point_excursion_name ?? "")
}

function getPriceRange(row: DayTripItem): { min: number; max: number } | null {
    const prices = SEATING_COLUMNS
        .map(({ seatingName }) => getPriceBySeating(row.day_trip_pricing, seatingName))
        .filter((price): price is number => price != null)
    if (prices.length === 0) return null
    return { min: Math.min(...prices), max: Math.max(...prices) }
}

type ConfirmType = "status" | "delete" | null

export interface StatusErrorItem {
    message: string
    id: string
    name: string
    flag_excursion: boolean
}

const DayTrip: React.FC = () => {
    const navigate = useNavigate()
    const { showSuccess, showError } = useSnackbar()
    const [dayTrips, setDayTrips] = useState<DayTripItem[]>([])
    const {
        search: searchValue, setSearch: setSearchValue,
        sortState, setSortState,
        filters: tableSelectedFilters, setFilters: setTableSelectedFilters,
        page, setPage, pageSize, setPageSize,
    } = useListView(LIST_VIEW_KEYS.trips)
    const handleColumnSort = React.useCallback(
        (key: string) => setSortState((prev) => cycleSort(prev, key)),
        [setSortState],
    )
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [confirmType, setConfirmType] = useState<ConfirmType>(null)
    const [selectedRow, setSelectedRow] = useState<DayTripItem | null>(null)
    const [dayTripUsageData, setDayTripUsageData] = useState<{ package_name: string }[]>([])
    const [dayTripUsageLoading, setDayTripUsageLoading] = useState(false)
    const [statusErrorData, setStatusErrorData] = useState<StatusErrorItem[] | null>(null)
    const [priceRangeRow, setPriceRangeRow] = useState<DayTripItem | null>(null)

    // ----- Filter accessors (Status, Editor, City, Trip Type per spec) -----
    const FILTER_ACCESSORS = useMemo(
        () => ({
            status: (row: DayTripItem) => (row.flag_active ? "Active" : "Inactive"),
            editor: (row: DayTripItem) => row.updated_by_user_name || "",
            city: (row: DayTripItem) => row.city_name || "",
            tripType: (row: DayTripItem) => row.day_trip_type_name || "",
        }),
        [],
    )

    const filterGroups: FilterGroup[] = useMemo(
        () => [
            {
                key: "status",
                label: "Status",
                options: [
                    { label: "Active", value: "Active" },
                    { label: "Inactive", value: "Inactive" },
                ],
            },
            { key: "city", label: "City", options: uniqueOptionsFromRows(dayTrips, FILTER_ACCESSORS.city) },
            { key: "tripType", label: "Trip Type", options: uniqueOptionsFromRows(dayTrips, FILTER_ACCESSORS.tripType) },
            { key: "editor", label: "Editor", options: uniqueOptionsFromRows(dayTrips, FILTER_ACCESSORS.editor) },
        ],
        [dayTrips, FILTER_ACCESSORS],
    )

    // Defer heavy table re-render so clearing search/filters doesn't block the UI.
    const deferredSearch = useDeferredValue(searchValue)
    const deferredFilters = useDeferredValue(tableSelectedFilters)
    const filteredDayTrips = useMemo(() => {
        let rows = applyFilters(dayTrips, deferredFilters, FILTER_ACCESSORS)
        rows = applySearch(rows, deferredSearch, [(r) => r.day_trip_name])
        rows = sortRowsBy(rows, sortState)
        return rows
    }, [dayTrips, deferredFilters, deferredSearch, sortState, FILTER_ACCESSORS])

    const paginationQueryKey = JSON.stringify({ search: searchValue, filters: tableSelectedFilters, sortState })
    const {
        pagedRows: pagedDayTrips,
        total: pagedTotal,
        page: currentPage,
        pageSize: currentPageSize,
        handlePageChange,
        handlePageSizeChange,
    } = useClientListPagination(filteredDayTrips, {
        page, pageSize, setPage, setPageSize, queryKey: paginationQueryKey,
    })

    const fetchDayTripList = async () => {
        try {
            const response = await agent.DayTripListAPI.get()
            if (response?.status === true && Array.isArray(response?.data)) {
                setDayTrips(response.data)
            } else {
                setDayTrips([])
            }
        } catch (error) {
            console.error("fetchDayTripList Error", error)
            setDayTrips([])
        }
    }

    useEffect(() => {
        fetchDayTripList()
    }, [])

    const openStatusConfirm = (row: DayTripItem) => {
        setSelectedRow(row)
        setConfirmType("status")
        setConfirmOpen(true)
    }

    const openDeleteConfirm = (row: DayTripItem) => {
        setSelectedRow(row)
        setConfirmType("delete")
        setDayTripUsageLoading(true)
        agent.CheckDayTripUsageAPI.post({ day_trip_id: row.day_trip_id }, true)
            .then((response) => {
                if (response?.status === true && Array.isArray(response?.data)) {
                    setDayTripUsageData(response.data)
                } else {
                    setDayTripUsageData([])
                }
            })
            .catch(() => setDayTripUsageData([]))
            .finally(() => {
                setDayTripUsageLoading(false)
                setConfirmOpen(true)
            })
    }

    const closeConfirm = () => {
        setConfirmOpen(false)
        setConfirmType(null)
        setSelectedRow(null)
        setDayTripUsageData([])
    }

    const handleConfirmAction = async () => {
        const row = selectedRow
        const type = confirmType
        closeConfirm()
        if (!row || !type) return
        if (type === "status") {
            try {
                const res = await agent.DayTripDeleteAPI.put({
                    day_trip_id: row.day_trip_id,
                    flag_active: !row.flag_active,
                    day_trip_flag_deleted: false,
                })
                if (res?.status === false) {
                    const errorData = res?.data ?? res?.error?.data
                    if (res?.error?.code === 400 && Array.isArray(errorData) && errorData.length > 0) {
                        setStatusErrorData(
                            errorData.map((item: { message?: string; id?: string; name?: string; flag_excursion?: boolean }) => ({
                                message: item.message ?? "",
                                id: item.id ?? "",
                                name: item.name ?? "",
                                flag_excursion: item.flag_excursion ?? false,
                            }))
                        )
                        return
                    }
                    showError(res?.error?.message ?? "Failed to update status")
                    return
                }
                showSuccess("Status updated successfully")
                fetchDayTripList()
            } catch (e) {
                console.error("handleToggleStatus error", e)
                showError("Failed to update status")
            }
        } else if (type === "delete") {
            try {
                const res = await agent.DayTripDeleteAPI.put({
                    day_trip_id: row.day_trip_id,
                    flag_active: row.flag_active,
                    day_trip_flag_deleted: true,
                })
                if (res?.status === false) {
                    showError(res?.error?.message ?? "Failed to delete day trip")
                    return
                }
                showSuccess("Day trip deleted successfully")
                fetchDayTripList()
            } catch (e) {
                console.error("handleDelete error", e)
                showError("Failed to delete day trip")
            }
        }
    }

    const columns: TableColumn<DayTripItem>[] = [
        {
            key: "day_trip_name",
            label: "Trip Name",
            width: "14%",
            align: "left",
            render: (value: string, row: DayTripItem) => (
                <span
                    className={styles.tripNameLink}
                    onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/transportation/day-trip/${row.day_trip_id}`)
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault()
                            navigate(`/transportation/day-trip/${row.day_trip_id}`)
                        }
                    }}
                >
                    {toTitleCase(value)}
                </span>
            ),
        },
        {
            key: "city_name",
            label: "City",
            width: "10%",
            align: "left",
            render: (value: string) => toTitleCase(value),
        },
        {
            key: "day_trip_type_name",
            label: "Trip Type",
            width: "10%",
            align: "left",
        },
        {
            key: "route",
            label: "Route",
            width: "18%",
            align: "left",
            render: (_: unknown, row: DayTripItem) => `${toTitleCase(getStartPointName(row))} → ${toTitleCase(getEndPointName(row))}`,
        },
        {
            key: "stops",
            label: "Stops",
            width: "6%",
            align: "center",
            render: (_: unknown, row: DayTripItem) => row.day_trip_stop?.length ?? 0,
        },
        {
            key: "priceRange",
            label: "Price Range",
            width: "10%",
            align: "right",
            sortable: false,
            render: (_: unknown, row: DayTripItem) => {
                const range = getPriceRange(row)
                if (!range) return "—"
                const label = range.min === range.max ? formatPrice(range.min) : `${formatPrice(range.min)}-${formatPrice(range.max)}`
                return (
                    <span
                        className={styles.tripNameLink}
                        onClick={(e) => {
                            e.stopPropagation()
                            setPriceRangeRow(row)
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault()
                                setPriceRangeRow(row)
                            }
                        }}
                    >
                        {label}
                    </span>
                )
            },
        },
        {
            key: "flag_active",
            label: "Status",
            width: "8%",
            align: "center",
            render: (value: boolean) => <span className={value === true ? utilStyles.statusBadgeActive : value === false ? utilStyles.statusBadgeInactive : utilStyles.statusBadgeInactive}>{value ? "Active" : "Inactive"}</span>,
        },
        {
            key: "actions",
            label: "",
            width: "5%",
            align: "center",
            render: (_: unknown, row: DayTripItem) => (
                <CustomizedMenu
                    buttonText=""
                    menuItems={[
                        {
                            label: row.flag_active ? "Set Status to Inactive" : "Set Status to Active",
                            onClick: () => openStatusConfirm(row),
                        },
                        {
                            label: "Delete",
                            onClick: () => openDeleteConfirm(row),
                        },
                    ]}
                    buttonVariant="text"
                    buttonStyle={{ padding: "0px", minWidth: "0px" }}
                    buttonEndIcon={<IconsColor iconName="moreVert" width="20" height="20" fillColor="none" strokeColor="#7a7a7a" />}
                />
            ),
        },
    ]

    return (
        <>
            {dayTripUsageLoading && <Loader message="Checking usage..." overlay={true} />}
        <div className={`${styles.datTripWrapper} ${utilStyles.listPageFill}`}>
            <div className={utilStyles.listPageHeader}>
                    <MainTabsTableHeader
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        searchPlaceholder="Search trips"
                        totalCount={dayTrips.length}
                        filteredCount={filteredDayTrips.length}
                        itemLabel="trips"
                        sortValue=""
                        onSortChange={() => undefined}
                        sortOptions={[]}
                        filterGroups={filterGroups}
                        selectedFilters={tableSelectedFilters}
                        onFiltersChange={setTableSelectedFilters}
                        addButtonLabel="Day Trip"
                        onAddClick={() => navigate("/transportation/day-trip/new")}
                        showResultCount={false}
                    />
            </div>
            <div className={utilStyles.listPageTableBlock}>
                <div className={utilStyles.listPageTableScroll}>
                    <Table
                        columns={columns}
                        data={pagedDayTrips}
                        keyExtractor={(row) => row.day_trip_id}
                        emptyMessage="No day trips available"
                        minWidth={1000}
                        fillHeight={true}
                        sortKey={sortState.key}
                        sortDirection={sortState.direction}
                        onSort={handleColumnSort}
                    />
                </div>
                <TablePaginationBar
                    total={pagedTotal}
                    page={currentPage}
                    pageSize={currentPageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>
            <Popup
                isOpen={!!statusErrorData}
                onClose={() => setStatusErrorData(null)}
                title="Cannot change status"
                primaryButton={{ label: "OK", onClick: () => setStatusErrorData(null) }}
                heightFiexd={true}
            >
                {statusErrorData && statusErrorData.length > 0 ? (
                    <div className={styles.confirmationContent}>
                        <IconsColor iconName="warningSign" width="80px" height="80px" fillColor="none" strokeColor="#FF0000" />
                        <p className={styles.confirmationText}>The following places or excursions are missing or inactive:</p>
                        <div className={styles.dayTripUsageWarning}>
                            <ul className={styles.dayTripUsageList}>
                                {statusErrorData.map((item, index) => (
                                    <li key={`${item.id}-${index}`}>
                                        <strong>{item.name}</strong> — {item.message}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                ) : null}
            </Popup>
            <Popup isOpen={confirmOpen} onClose={closeConfirm} title={confirmType === "status" ? "Change Day Trip Status" : "Delete Day Trip"} primaryButton={{ label: "Confirm", onClick: handleConfirmAction }} secondaryButton={{ label: "Cancel", onClick: closeConfirm }} heightFiexd={true}>
                {selectedRow && confirmType === "status" ? (
                    <div className={styles.confirmationContent}>
                        <IconsColor iconName="warningSign" width="80px" height="80px" fillColor="none" strokeColor="#FF0000" />
                        <p className={styles.confirmationText}>
                            Are you sure you want to change the status of <strong>{selectedRow.day_trip_name}</strong> to <strong>{selectedRow.flag_active ? "Inactive" : "Active"}</strong>?
                        </p>
                    </div>
                ) : selectedRow && confirmType === "delete" ? (
                    <div className={styles.confirmationContent}>
                        <IconsColor iconName="warningSign" width="80px" height="80px" fillColor="none" strokeColor="#FF0000" />
                        <p className={styles.confirmationText}>
                            Are you sure you want to delete <strong>{selectedRow.day_trip_name}</strong>?
                        </p>
                        {dayTripUsageData.length > 0 ? (
                            <div className={styles.dayTripUsageWarning}>
                                <p className={styles.dayTripUsageWarningTitle}>
                                    Listed packages are going to Inactive on Delete:
                                </p>
                                <ul className={styles.dayTripUsageList}>
                                    {dayTripUsageData.map((item, index) => (
                                        <li key={index}>{item.package_name}</li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </Popup>
            <Popup
                isOpen={!!priceRangeRow}
                onClose={() => setPriceRangeRow(null)}
                title={priceRangeRow ? `${toTitleCase(priceRangeRow.day_trip_name)} - Pricing` : "Pricing"}
                heightFiexd={true}
                size="large"
            >
                {priceRangeRow && (
                    <Table
                        columns={[
                            ...SEATING_COLUMNS.map(({ seatingName }) => ({
                                key: seatingName,
                                label: seatingName,
                                align: "center" as const,
                                sortable: false,
                                render: () => formatPrice(getPriceBySeating(priceRangeRow.day_trip_pricing, seatingName)),
                            })),
                            ...(priceRangeRow.flag_enable_sic
                                ? [
                                    {
                                        key: "SIC",
                                        label: "SIC",
                                        align: "center" as const,
                                        sortable: false,
                                        render: () => {
                                            const sicFromPricing = getPriceBySeating(priceRangeRow.day_trip_pricing, "SIC")
                                            const sicValue = sicFromPricing != null ? sicFromPricing : priceRangeRow.sic_price_per_person
                                            return formatPrice(sicValue ?? null)
                                        },
                                    },
                                ]
                                : []),
                        ]}
                        data={[priceRangeRow]}
                        keyExtractor={(row) => row.day_trip_id}
                        containerClassName={styles.pricingPopupTable}
                    />
                )}
            </Popup>
        </div>
        </>
    )
}

export default DayTrip
